import React, { FC, useContext } from 'react';
import './Shift.scss';
import { ShiftStatusType } from 'const/ShiftConst';
import clsx from 'clsx';
import { IShift } from './shiftApi';
import { CarType, CourierJson } from 'types/bundleTypes';
import { Dayjs } from 'dayjs';
import ActionPanel, { ShiftColor } from './ActionPanel';
import { RoleIcons } from './smallerComponents';
import { TooltipComponent } from '../../ReusableComponents/Tooltip/TooltipComponent';
import { ShiftPlannerContext } from './ShiftPlanner';

const shiftColorMap = {
  [ShiftStatusType.BOOKED]: ShiftColor.GREEN,
  [ShiftStatusType.REQUESTED]: ShiftColor.YELLOW,
  [ShiftStatusType.DECLINED]: ShiftColor.RED,
  [ShiftStatusType.DISPATCH_DECLINED]: ShiftColor.PURPLE,
  [ShiftStatusType.CANCELED]: ShiftColor.GREY,
  [ShiftStatusType.OPEN]: ShiftColor.BLUE,
};

const Shift: FC<{
  date: Dayjs;
  courier?: CourierJson;
  shiftData: IShift;
  wishList: number[];
  couriers: CourierJson[];
}> = ({ date, courier, shiftData, wishList, couriers }) => {
  const { shiftCount } = useContext(ShiftPlannerContext);
  const shiftColor = shiftColorMap[shiftData.status];
  const courierName = courier?.fullName ?? '';
  const isElectric = courier?.carType === CarType.ELECTRIC;
  const shiftBackground = shiftData.published ? 'bgcolor' : 'unpublished';

  const bookedRequestedShiftsCount = (shiftWeek, courier) => {
    if (!shiftWeek[courier?.id]) return 0;
    return shiftWeek[courier?.id]?.booked + shiftWeek[courier?.id]?.requested;
  };

  return shiftData.status === ShiftStatusType.OPEN ? (
    <OpenShift
      backgroundType={shiftBackground}
      date={date}
      shiftData={shiftData}
      wishList={wishList}
      couriers={couriers}
    />
  ) : (
    <div
      className={clsx(
        'newShiftCalendar__shift',
        `newShiftCalendar__${shiftBackground}__${ShiftColor[shiftColor]}`
      )}
    >
      <RoleIcons
        className="newShiftCalendar__shift__transport"
        vehicleType={courier?.isCourier && courier?.travelMode}
        isElectric={isElectric}
        isScanner={courier?.isSorter}
      />
      <div className="newShiftCalendar__shift__body">
        {courierName.length > 16 ? (
          <TooltipComponent
            className="custom-tooltip-container"
            id="courier name"
            title={courierName}
            placement="top-start"
          >
            <h1>{courierName}</h1>
          </TooltipComponent>
        ) : (
          <h1>{courierName}</h1>
        )}
        <div className="newShiftCalendar__shift__body__details">
          <p>
            <TooltipComponent
              className="custom-tooltip-container"
              id="last week"
              title="Last week shifts"
              placement="top-start"
            >
              <span>L:</span>
            </TooltipComponent>{' '}
            {bookedRequestedShiftsCount(shiftCount.previousWeek, courier)}
          </p>
          <p>
            <TooltipComponent
              className="custom-tooltip-container"
              id="current week"
              title="Current week shifts"
              placement="top-start"
            >
              <span>C:</span>
            </TooltipComponent>{' '}
            {bookedRequestedShiftsCount(shiftCount.currentWeek, courier)}
          </p>
          <ActionPanel
            date={date}
            shiftData={shiftData}
            courier={courier}
            shiftColor={shiftColor}
            wishList={wishList}
            couriers={couriers}
          />
        </div>
      </div>
    </div>
  );
};

const OpenShift: FC<{
  backgroundType: string;
  date: Dayjs;
  shiftData: IShift;
  wishList: number[];
  couriers: CourierJson[];
}> = ({ backgroundType, date, shiftData, wishList, couriers }) => (
  <div
    className={clsx(
      'newShiftCalendar__openShift',
      `newShiftCalendar__${backgroundType}__BLUE`
    )}
  >
    <p>Open shift</p>
    <ActionPanel
      date={date}
      shiftData={shiftData}
      shiftColor={ShiftColor.BLUE}
      wishList={wishList}
      couriers={couriers}
    />
  </div>
);

export default Shift;
